import { QUOTE_CLASS_OPTIONS } from "constants/quoteQuestions";

// 견적요청 카테고리 8종 (네이티브 위저드 QuoteCategory 와 1:1)
export const QUOTE_CATEGORIES = [
  { id: "ballet", label: "발레" },
  { id: "korean", label: "한국무용" },
  { id: "modern", label: "현대무용" },
  { id: "practical", label: "실용무용" },
  { id: "musical", label: "뮤지컬" },
  { id: "barre", label: "바레" },
  { id: "balletfit", label: "발레핏" },
  { id: "hobby", label: "취미무용" },
];

export const quoteCategoryLabel = (id) =>
  QUOTE_CATEGORIES.find((c) => c.id === id || c.label === id)?.label || id || "레슨";

// 홈 장르 배지와 동일한 팔레트. 견적 목록에서도 전공을 색으로 바로 구분한다.
export const QUOTE_CATEGORY_COLORS = {
  ballet: { bg: "#F4ECE2", fg: "#A9793F" },
  korean: { bg: "#E7F4EA", fg: "#3E9D59" },
  modern: { bg: "#EEE8FB", fg: "#7B5CC4" },
  practical: { bg: "#FDE9DA", fg: "#E0752A" },
  barre: { bg: "#E6F0FD", fg: "#3B7BD4" },
  balletfit: { bg: "#FDE6E6", fg: "#D8564D" },
  musical: { bg: "#FFF1D6", fg: "#B7791F" },
  hobby: { bg: "#E8F3F1", fg: "#347F75" },
};
// 헤더 제목용 — 카테고리 + '레슨'. 라벨이 이미 '레슨'이면 '레슨 레슨' 중복 방지.
export const quoteCategoryTitle = (id) => {
  const label = quoteCategoryLabel(id);
  return label === "레슨" ? "레슨" : `${label} 레슨`;
};

// 견적요청 상태 (cls = 기존 .level 변형: high/middle/row/very-row/very-high)
export const QUOTE_STATUS = {
  OPEN: { label: "견적 받는 중", cls: "middle" },
  MATCHED: { label: "매칭 완료", cls: "high" },
  CLOSED: { label: "완료", cls: "very-row" },
  CANCELED: { label: "취소됨", cls: "very-high" },
  EXPIRED: { label: "견적 마감", cls: "very-high" }, // 생성 14일 경과 → 마감
};
export const quoteStatusLabel = (s) => QUOTE_STATUS[s]?.label || (s ? "상태 확인" : "");
export const quoteStatusClass = (s) => QUOTE_STATUS[s]?.cls || "middle";

// 견적 응답 상태
export const QUOTE_RESPONSE_STATUS = {
  SENT: { label: "견적 도착", cls: "middle" },
  ACCEPTED: { label: "수락함", cls: "row" },
  REJECTED: { label: "거절함", cls: "very-high" },
};
export const quoteResponseStatusLabel = (s) => QUOTE_RESPONSE_STATUS[s]?.label || (s ? "상태 확인" : "");

// 같은 상태를 '보낸 쪽(강사·학원)' 시점으로 읽은 라벨.
//  위 QUOTE_RESPONSE_STATUS 는 받는 쪽(고객) 문구라 보낸 견적서 화면에 그대로 쓰면
//  내가 보낸 견적에 '견적 도착'이 붙는다.
export const QUOTE_RESPONSE_STATUS_PRO = {
  SENT: { label: "답변 기다리는 중", cls: "middle" },
  ACCEPTED: { label: "채택됨", cls: "row" },
  REJECTED: { label: "거절됨", cls: "very-high" },
};
export const proResponseStatusLabel = (s) => QUOTE_RESPONSE_STATUS_PRO[s]?.label || "보낸 제안";
export const proResponseStatusClass = (s) => QUOTE_RESPONSE_STATUS_PRO[s]?.cls || "middle";

// 문진 질문/선택지 라벨 (네이티브 QuoteQuestion.common 과 동기화) — 전문가 요청 열람용
const QUESTION_TITLES = {
  class: "수업", age: "연령대", format: "레슨 형태", days: "선호 요일", time: "시간대",
  gender: "성별", teacherGender: "선호 강사 성별", when: "레슨 시기", region: "지역",
  method: "진행 방식", inquiry: "문의", detail: "추가 요청",
};
const OPTION_LABELS = {
  age: { preschool: "미취학", elem: "초등", middle: "중등", high: "고등", "20s": "20대", "30s": "30대", "40s+": "40대+" },
  format: { private: "개인", group: "그룹", academy: "학원", any: "무관", etc: "기타" },
  days: { mon: "월", tue: "화", wed: "수", thu: "목", fri: "금", sat: "토", sun: "일" },
  time: { dawn: "이른오전", morning: "오전", noon: "오후", afternoon: "늦은오후", evening: "저녁", night: "늦은저녁" },
  gender: { male: "남", female: "여" },
  teacherGender: { any: "무관", male: "남", female: "여" },
  when: { negotiable: "협의가능", asap: "가능한빨리", week: "일주일내", date: "지정일", etc: "기타" },
  method: { visitMe: "내 위치로", visitThem: "강사 위치로", any: "무관" },
  inquiry: { later: "상담시 논의", now: "지금 작성" },
};
const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/** 정본 질문 순서 — 서버 answers 는 클라 입력 순서라 요청마다 뒤죽박죽이다.
 *  네이티브 QuoteQuestion.describe 와 동일하게 이 순서로 정렬해 보여준다. */
const CANONICAL_ORDER = [
  "class", "age", "format", "days", "time", "gender", "teacherGender",
  "when", "region", "method", "inquiry", "detail",
];

/**
 * 문진 답변 배열 → 화면 표시용 [{ q, v }] (라벨 없는 값은 코드/원문 그대로).
 * @param answers    서버 quote.answers
 * @param categoryId 'class' 선택지는 카테고리별이라 라벨을 풀려면 필요하다.
 *                   (없으면 optionId 가 그대로 보인다 — 예: "adult")
 */
export function describeAnswers(answers, categoryId) {
  if (!Array.isArray(answers)) return [];
  const classMap = Object.fromEntries(
    (QUOTE_CLASS_OPTIONS[categoryId] || []).map(([value, text]) => [value, text]),
  );
  const idx = (qid) => {
    const i = CANONICAL_ORDER.indexOf(qid);
    return i === -1 ? CANONICAL_ORDER.length : i;
  };
  return answers
    .map((a, i) => {
      if (!a || !a.questionId) return null;
      const q = QUESTION_TITLES[a.questionId] || a.questionId;
      let v = "";
      if (Array.isArray(a.optionIds) && a.optionIds.length) {
        const map = a.questionId === "class" ? classMap : (OPTION_LABELS[a.questionId] || {});
        const optionIds = a.questionId === "days"
          ? [...a.optionIds].sort((left, right) => DAY_ORDER.indexOf(left) - DAY_ORDER.indexOf(right))
          : a.optionIds;
        v = optionIds.map((id) => map[id] || id).join(", ");
      } else if (a.region) v = a.region;
      else if (a.date) v = a.date;
      else if (a.text) v = a.text;
      return v ? { q, v, questionId: a.questionId, _i: i } : null;
    })
    .filter(Boolean)
    .sort((x, y) => (idx(x.questionId) - idx(y.questionId)) || (x._i - y._i));
}
